// JoshSkills page helpers (T-351, owner request 2026-11-03): data from the JoshSkills read-only API
// (joshskills/api.py on 127.0.0.1:10010; local dashboard reaches it via the Vite proxy rule '/api/joshskills').
// Pure functions are tested with node --test (frontend/tests/joshskills.test.mjs). No writes, no orders.

const BASE = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) || ''

/** GET a JoshSkills API path with the dashboard login token (the public relay requires it, T-351); throws with the
 *  server's error text on a non-2xx answer. The local Vite proxy ignores the header. */
export async function jsGet(path) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('sf_auth_token') : null
  const r = await fetch(`${BASE}/api/joshskills${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  const body = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`)
  return body
}

export const EXIT_TONE = { target: '#34d399', stop: '#f87171', flat_by: '#94a3b8', flat_lastbar: '#94a3b8' }
export const VERDICT_TONE = { 'toward target': '#34d399', 'coin flip': '#fbbf24', 'toward stop': '#f87171', unknown: '#94a3b8' }

/** Money with sign, e.g. +$12.30 / -$4.00; '—' for null. */
export function money(v) {
  if (v == null || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  return `${n < 0 ? '-' : '+'}$${Math.abs(n).toFixed(2)}`
}

/** R multiple with sign, e.g. +0.56R. */
export function rText(v) {
  if (v == null || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}R`
}

/** Seconds to add to a UTC epoch so lightweight-charts (which draws UTC) shows New York wall time. */
export function etShift(epochSec) {
  const d = new Date(epochSec * 1000)
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(d).filter(p => p.type !== 'literal').map(p => [p.type, p.value]))
  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second)
  return Math.round((asUtc - d.getTime()) / 1000)
}

/** Bars from the API -> candle data in New York wall time. */
export function toCandles(bars) {
  return (bars || []).map(b => ({ time: b.time + etShift(b.time), open: b.open, high: b.high, low: b.low, close: b.close }))
}

/** Entry / exit markers for one trade, snapped to the 5-minute bar that holds each moment (wall time). */
export function tradeMarkers(t) {
  const out = []
  const snap = (iso) => { const e = Math.floor(new Date(iso).getTime() / 1000); const b = e - (e % 300); return b + etShift(b) }
  if (t.entry_time) out.push({ time: snap(t.entry_time) - 300, position: 'belowBar', color: '#38bdf8', shape: 'arrowUp', text: `BUY ${Number(t.entry_price).toFixed(2)}` })
  if (t.exit_time) out.push({ time: snap(t.exit_time) - (t.exit_reason === 'flat_by' ? 0 : 300), position: 'aboveBar', color: EXIT_TONE[t.exit_reason] || '#94a3b8', shape: 'arrowDown', text: `${(t.exit_reason || '').toUpperCase()} ${Number(t.exit_price).toFixed(2)}` })
  return out.sort((a, b) => a.time - b.time)
}

/** Summary line for the stats strip. */
export function statsLine(s) {
  if (!s || !s.closed) return 'no closed trades yet'
  const wr = Math.round((s.wins / s.closed) * 100)
  return `${s.closed} closed · ${s.wins} won (${wr}%) · avg ${rText(s.avg_r)} · native ${money(s.pnl)} · could-have-filled ${money(s.alt_pnl)}`
}
